import React from "react";
import Dropdown from "./components/Dropdown/Dropdown";
import {hscLogo, summitLogo} from "./consts/consts";
import moment from "moment";

const cloneDeep = require('lodash.clonedeep');

/**
 * An Extra Payment is an add-on to a deal that already closed, not a closing of
 * its own, so the Summit closings lists leave it out — matching the stat cards
 * they are reached from (StatsByStatusQuery::IS_CLOSING). Property 96133.
 */
const EXTRA_PAYMENT = 'Extra Payment';

/**
 * Apply all query pre-processing shared between the List view and the Map view.
 *
 * Handles, in order:
 *  1. HOT_LIST → HOT_LIST_USER_EMAIL injection
 *  2. MAIN_CATEGORY shorthand → SUMMIT_* flags
 *  3. SUMMIT_* flags → SOURCE + STATUS + date-range defaults + default order
 *     (+ EXCLUDE_TRANSACTION_TYPE for the two closings views)
 *  4. Strive + Closed → CLOSE_OF_ESCROW default order
 *  5. Summit Active Investment Sales → LISTED_DATE default order
 *  6. Closed-filter → CLOSE_OF_ESCROW default order
 *  7. Final fallback order (ARCIMEDES_ROW_ID.desc)
 *  8. PRODUCT_TYPE → TYPE + CATEGORY decomposition (V10 / territory queries)
 *
 * Returns a new, fully-processed query object (the input is never mutated).
 */
export function preprocessQueryFilters(rawQuery, userData) {
    const q = { ...rawQuery };

    // 1. Inject user email for HOT_LIST queries
    if (q.HOT_LIST && userData && userData.email) {
        q.HOT_LIST_USER_EMAIL = userData.email;
    }

    // 2. Expand MAIN_CATEGORY shorthand → SUMMIT_* flags
    if (q.MAIN_CATEGORY) {
        const cat = q.MAIN_CATEGORY;
        delete q.MAIN_CATEGORY;
        if (cat === 'SUMMIT_YTD') q.SUMMIT_YTD = 'true';
        else if (cat === 'SUMMIT_CLOSINGS') q.SUMMIT_CLOSINGS = 'true';
        else if (cat === 'SUMMIT_NEGUC') q.SUMMIT_NEGUC = 'true';
        else if (cat === 'SUMMIT_INVENTORY') q.SUMMIT_INVENTORY = 'true';
    }

    // 3. Expand SUMMIT_* flags → SOURCE + STATUS + date defaults
    const isSummitYtd = !!q.SUMMIT_YTD;
    const isSummitClosings = !!q.SUMMIT_CLOSINGS;
    const isSummitNeguc = !!q.SUMMIT_NEGUC;
    const isSummitInventory = !!q.SUMMIT_INVENTORY;
    const isSummitFilter = isSummitYtd || isSummitClosings || isSummitNeguc;

    if (isSummitFilter || isSummitInventory) {
        delete q.SUMMIT_YTD;
        delete q.SUMMIT_CLOSINGS;
        delete q.SUMMIT_NEGUC;
        delete q.SUMMIT_INVENTORY;
        // SOURCE='SUMMIT': Closed / Neg+UC also include External rows whose
        // BUYER_FIRM is SUMMIT. Active inventory is SOURCE-column only (V7 SQL).
        q.SOURCE = 'SUMMIT';
    }

    if (isSummitYtd) {
        q.STATUS = 'Closed';
        q.CLOSE_OF_ESCROW_MIN = '01/01/' + new Date().getFullYear();
        q.EXCLUDE_TRANSACTION_TYPE = EXTRA_PAYMENT;
        if (!q.order) q.order = 'CLOSE_OF_ESCROW.desc';
    } else if (isSummitClosings) {
        q.STATUS = 'Closed';
        q.EXCLUDE_TRANSACTION_TYPE = EXTRA_PAYMENT;
        if (!q.order) q.order = 'CLOSE_OF_ESCROW.desc';
    } else if (isSummitNeguc) {
        q.STATUS = 'In Negotiation,Under Contract';
        q.TRANSACTION_TYPE = 'Investment Sales';
        if (!q.order) q.order = 'STATUS.asc';
    } else if (isSummitInventory) {
        // Strict Summit listings only — not External deals with a Summit buyside agent.
        q.STATUS = 'Active';
        if (!q.order) q.order = 'LISTED_DATE.desc';
    }

    // 4. Strive + Closed → default CLOSE_OF_ESCROW order
    if (
        q.SOURCE &&
        q.SOURCE.toLowerCase() === 'strive' &&
        ((q.STATUS && q.STATUS.toLowerCase() === 'closed') ||
            (q.HSC_STATUS && q.HSC_STATUS.toLowerCase() === 'closed'))
    ) {
        if (Array.isArray(q.order) && q.order.indexOf('CLOSE_OF_ESCROW.desc') === -1) {
            q.order = [...q.order, 'CLOSE_OF_ESCROW.desc'];
        } else if (!q.order) {
            q.order = 'CLOSE_OF_ESCROW.desc';
        }
    }

    // 5. Summit Active Investment Sales → default LISTED_DATE order
    if (!q.order && q.SOURCE === 'SUMMIT' && q.STATUS === 'Active' && q.TRANSACTION_TYPE === 'Investment Sales') {
        q.order = 'LISTED_DATE.desc';
    }

    // 6. Any closed-style filter → default CLOSE_OF_ESCROW order
    const isClosedFilter = q.CLOSE_OF_ESCROW_MIN || q.CLOSE_OF_ESCROW_MAX || q.STATUS === 'Closed' || q.HSC_STATUS === 'Closed';
    if (!q.order && isClosedFilter) {
        q.order = 'CLOSE_OF_ESCROW.desc';
    }

    // 7. Final fallback order
    if (!q.order) {
        q.order = 'ARCIMEDES_ROW_ID.desc';
    }

    // 8. PRODUCT_TYPE → TYPE + CATEGORY decomposition (V10 territory path only).
    Object.assign(q, productTypeFilters(q));

    return q;
}

const LEGACY_TERRITORY_PLACEHOLDERS = ['Territory1', 'Territory2', 'Territory3', 'Territory4'];

/** A real agent picked in the territory filter, not one of the legacy placeholders. */
function isTerritoryAgentFilter(q) {
    return q.hasOwnProperty('TERRITORY_LISTING_AGENT_TEAM') &&
        !LEGACY_TERRITORY_PLACEHOLDERS.includes(q.TERRITORY_LISTING_AGENT_TEAM);
}

/**
 * The TYPE and CATEGORY a territory list's PRODUCT_TYPE stands for. Not on the
 * agent (V9) path, which intentionally leaves PRODUCT_TYPE undecomposed.
 */
function productTypeFilters(q) {
    if (
        isTerritoryAgentFilter(q) ||
        !(q.hasOwnProperty('PRODUCT_TYPE') || q.hasOwnProperty('TERRITORY') || q.hasOwnProperty('TERRITORY_MARKET'))
    ) {
        return {};
    }

    const types = new Set();
    const categories = new Set();
    if (q.PRODUCT_TYPE && q.PRODUCT_TYPE !== '') {
        q.PRODUCT_TYPE.split(',').forEach(product => {
            const parts = product.split(' ');
            if (parts.length === 2) {
                types.add(parts[0]);
                categories.add(parts[1]);
            }
        });
    }
    const type = [...types].join(',');
    const category = [...categories].join(',');
    if (types.size > 0) return { TYPE: type, CATEGORY: category };
    if (categories.size > 0) return { CATEGORY: category };

    return {};
}

/** True when the list is filtered to In Negotiation and/or Under Contract only. */
/** True however a flag arrives: a boolean from the API, 1 from a raw query, "1" from the Arc gateway. */
export function isOn(flag) {
	return [true, 1, '1'].includes(flag);
}

export function isNegotiationStatusFilter(query) {
    if (!query) return false;
    if (query.SUMMIT_NEGUC === 'true' || query.SUMMIT_NEGUC === true) return true;
    if (query.MAIN_CATEGORY === 'SUMMIT_NEGUC') return true;
    if (!query.STATUS) return false;
    const status = query.STATUS.split(',').map((s) => s.trim()).filter(Boolean);
    let count = 0;
    if (status.indexOf('In Negotiation') !== -1) count++;
    if (status.indexOf('Under Contract') !== -1) count++;
    return count > 0 && count === status.length;
}

/**
 * Media files are stored in the DB as bucket-relative paths (e.g. "training/photo.jpg").
 * toMediaUrl builds the absolute GCS URL used for rendering. It is idempotent: a value
 * that is already an absolute URL is returned unchanged rather than being prefixed a
 * second time — this prevents the doubled "https://.../https://.../..." src that can
 * occur after the Firebase→GCS migration when a row already holds a full URL.
 */
export function toMediaUrl(path) {
    return !path || /^https?:\/\//i.test(path) ? path : `${arc.path.media}${path}`;
}

/**
 * Inverse of toMediaUrl, used before persisting: strips the bucket prefix so the DB
 * always stores a relative path, whether the input is absolute or already relative.
 */
export function toMediaPath(url) {
    return url && url.startsWith(arc.path.media) ? url.slice(arc.path.media.length) : url;
}

/**
 * Hand-off of a user-narrowed property set ("Remove Selected" / "Remove
 * Unselected") between the list and map views, which are separate routes and so
 * cannot share component state.
 *
 * The baton is single-use: whichever view arrives consumes it. That keeps a hard
 * refresh or a fresh visit fetching normally, while a list <-> map round trip
 * carries the narrowing across in both directions.
 */
const NARROWED_HANDOFF_KEY = 'narrowedHandoffItems';

export function writeNarrowedHandoff(items) {
    try {
        if (items && items.length) {
            sessionStorage.setItem(NARROWED_HANDOFF_KEY, JSON.stringify(items));
        } else {
            sessionStorage.removeItem(NARROWED_HANDOFF_KEY);
        }
    } catch (e) {
        // Quota or disabled storage: the narrowing is a convenience, never fail navigation over it.
    }
}

export function readNarrowedHandoff() {
    let raw = null;
    try {
        raw = sessionStorage.getItem(NARROWED_HANDOFF_KEY);
        sessionStorage.removeItem(NARROWED_HANDOFF_KEY);
    } catch (e) {
        return null;
    }
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) && parsed.length ? parsed : null;
    } catch (e) {
        return null;
    }
}

export function clearNarrowedHandoff() {
    try {
        sessionStorage.removeItem(NARROWED_HANDOFF_KEY);
    } catch (e) {
        // ignore
    }
}

/**
 * The property set (or, from the Vendors and Agents lists, the contacts) the
 * mail merge page works from, plus the filters it came from and the draft
 * being written against it.
 *
 * Deliberately not the single-use baton above: a broker spends real time
 * writing an email, and a reload halfway through must not throw away either
 * the list or the words. Cleared when they leave the page for good.
 */
const MAIL_MERGE_KEY = 'mailMergeSelection';

export function writeMailMergeSelection(selection) {
    try {
        sessionStorage.setItem(MAIL_MERGE_KEY, JSON.stringify(selection));
    } catch (e) {
        // Quota or disabled storage: the page still works from what it holds
        // in memory, it just cannot survive a reload.
    }
}

export function readMailMergeSelection() {
    try {
        const parsed = JSON.parse(sessionStorage.getItem(MAIL_MERGE_KEY));
        // A vendor or agent merge names its contacts instead of any property.
        const nonEmpty = (ids) => Array.isArray(ids) && ids.length > 0;
        return parsed && (nonEmpty(parsed.propertyIds) || nonEmpty(parsed.contactIds)) ? parsed : null;
    } catch (e) {
        return null;
    }
}

export function clearMailMergeSelection() {
    try {
        sessionStorage.removeItem(MAIL_MERGE_KEY);
    } catch (e) {
        // ignore
    }
}

/**
 * Map PEAK_* URL params to HSC_* for internal/API use (pipeline links use PEAK_* in URL only).
 */
/** Map legacy owner sort codes without rewriting other list filters. */
export function normalizeListOrder(order) {
    if (order == null) return order;
    const orders = Array.isArray(order)
        ? order
        : String(order).split(',').map((s) => s.trim()).filter(Boolean);
    const mapped = orders.map((o) =>
        o.replace(/^owner_links\./, 'CURRENT_OWNER_NAME.')
         .replace(/^PEAK_STATUS\./, 'HSC_STATUS.')
    );
    return mapped.length === 1 ? mapped[0] : mapped;
}

/**
 * The list filters the parcel map can honour, translated to its query keys.
 * CITY is left out on purpose: the list matches it as free text (LIKE), and
 * the map's exact filter would silently drop everything on a partial term.
 */
const PARCEL_MAP_FILTERS = {
    STATUS: 'status',
    SOURCE: 'source',
    MARKET: 'market',
    CATEGORY: 'category',
    TYPE: 'type',
    STATE: 'state',
    ZIP: 'zip',
    TERRITORY_LISTING_AGENT_TEAM: 'territory_agent',
    TERRITORY: 'territory',
    TERRITORY_MARKET: 'territory_market',
    TERRITORY_ZIP: 'territory_zip',
};

export function parcelMapQuery(listQuery) {
    const query = {};
    // A plain copy first: the router's query object has no prototype, so
    // hasOwnProperty is not there to call on it.
    const list = { ...(listQuery || {}) };

    Object.assign(list, productTypeFilters(list));

    // The placeholders name no agent, and the list ignores them too.
    if (!isTerritoryAgentFilter(list)) delete list.TERRITORY_LISTING_AGENT_TEAM;

    Object.keys(PARCEL_MAP_FILTERS).forEach((key) => {
        const value = list[key];
        if (value != null && value !== '') query[PARCEL_MAP_FILTERS[key]] = value;
    });

    return query;
}

/**
 * Target List types that stand for a filter the list already supports. The
 * link carries the name; the window it means is resolved here so the URL
 * cannot drift from what the list actually filters on.
 */
export const TARGET_LIST_MATURING_LOANS = 'maturing-loans';

const padUS = (n) => String(n).padStart(2, '0');

/** MM/DD/YYYY — the format the list's date filters parse (dateUStoISO). */
function formatUSDate(date) {
    return `${padUS(date.getMonth() + 1)}/${padUS(date.getDate())}/${date.getFullYear()}`;
}

/**
 * The Maturing Loans window: today through the same day 12 months out.
 * Day-of-month is clamped so 03/31 + 12mo stays in the target month rather
 * than rolling into the next one the way Date month arithmetic would.
 */
export function maturingLoansWindow(from = new Date()) {
    const end = new Date(from.getFullYear() + 1, from.getMonth() + 1, 0);
    end.setDate(Math.min(from.getDate(), end.getDate()));

    return {
        MATURITY_DATE_MIN: formatUSDate(from),
        MATURITY_DATE_MAX: formatUSDate(end),
    };
}

/**
 * The Peak status a list query carries, under either spelling. URLs now use
 * PEAK_STATUS; HSC_STATUS is still accepted so old links and bookmarks work.
 */
export function peakStatusOf(query) {
    if (!query) return undefined;
    return query.PEAK_STATUS != null ? query.PEAK_STATUS : query.HSC_STATUS;
}

/**
 * Inverse of the Peak aliasing in `normalizeListQuery`: the internal `HSC_*`
 * keys are what every payload and column name still uses, but a URL must only
 * ever show Peak. Apply this to a query right before it is pushed to history.
 */
export function peakifyListQuery(query) {
    if (!query) return query;
    const q = { ...query };
    if (q.SOURCE === 'HSC') {
        q.SOURCE = 'PEAK';
    }
    if (q.HSC_STATUS != null) {
        q.PEAK_STATUS = q.HSC_STATUS;
        delete q.HSC_STATUS;
    }
    if (q.HSC_TEAM_MEMBERS != null) {
        const team = q.HSC_TEAM_MEMBERS;
        q.PEAK_TEAM_MEMBERS = typeof team === 'string'
            ? team.replace(/@highstcapital\.com/gi, '@peakcapital.io')
            : team;
        delete q.HSC_TEAM_MEMBERS;
    }
    if (q.order != null) {
        const orders = (Array.isArray(q.order) ? q.order : [q.order])
            .map((o) => String(o).replace(/^HSC_STATUS\./, 'PEAK_STATUS.'));
        q.order = orders.length === 1 ? orders[0] : orders;
    }
    return q;
}

export function normalizeListQuery(locationQuery) {
    if (!locationQuery) return locationQuery;
    const q = { ...locationQuery };
    if (q.type === TARGET_LIST_MATURING_LOANS) {
        const window = maturingLoansWindow();
        delete q.type;
        // An explicit window in the URL wins, so the filter panel can still
        // narrow the range without the link snapping it back.
        if (!q.MATURITY_DATE_MIN) q.MATURITY_DATE_MIN = window.MATURITY_DATE_MIN;
        if (!q.MATURITY_DATE_MAX) q.MATURITY_DATE_MAX = window.MATURITY_DATE_MAX;
    }
    // A legacy link may still say SOURCE=HSC. Canonicalize to PEAK so the filter
    // dropdown matches an option and the URL settles on one spelling; filter.js
    // maps PEAK back to the stored 'HSC' for the payload either way.
    if (q.SOURCE === 'HSC') {
        q.SOURCE = 'PEAK';
    }
    if (q.PEAK_STATUS != null) {
        q.HSC_STATUS = q.PEAK_STATUS;
        delete q.PEAK_STATUS;
    }
    if (q.PEAK_TEAM_MEMBERS != null) {
        // URL shows peakcapital.io; payload must use highstcapital.com for API/DB
        const peakTeam = q.PEAK_TEAM_MEMBERS;
        q.HSC_TEAM_MEMBERS = typeof peakTeam === 'string'
            ? peakTeam.replace(/@peakcapital\.io/gi, '@highstcapital.com')
            : peakTeam;
        delete q.PEAK_TEAM_MEMBERS;
    }
    if (q.order != null) {
        q.order = normalizeListOrder(q.order);
    }
    return q;
}

export { isPeakUserEmail } from './peak-user';

/** Normalize Peak team member emails for comparison (@peakcapital.io vs @highstcapital.com). */
export function normalizePeakEmail(email) {
    if (email == null || email === '') return '';
    return String(email).trim().toLowerCase().replace(/@peakcapital\.io$/i, '@highstcapital.com');
}

/** True when the signed-in user is Peak Team Member 1 on the property. */
export function isPeakTeamMember1(userEmail, teamMember1Email) {
    const normalizedUser = normalizePeakEmail(userEmail);
    const normalizedMember = normalizePeakEmail(teamMember1Email);
    return Boolean(normalizedUser && normalizedMember && normalizedUser === normalizedMember);
}

export function convertPhoneNumber(phone) {
    if (!phone) {
        return '';
    }
    const p = phone.split(/[()\- ]/g);
    return `${p[1]}.${p[3]}.${p[4]}`
}

export function addCommasToNumberAsString(string) {
    if (!string)
        return '';
    let parts = string.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join('.');
}

export function addCommasToNumber(number) {
    if (!number || !number.toString())
        return number;
    let parts = number.toString().split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join('.');
}
export function formatEmail(email) {
    var parts = email.split('@');
    var username = parts[0].substr(0, 2).toUpperCase() + parts[0].substr(2);
    var domain = email.includes('@') ? parts[1].replace(/(summitre)(?=\.com$)/i, 'SUMMITre') : '';
    return username + '@' + domain;
}

// data is array of objects
export function calculateTotalColumn(data, key, count = false) {
    return data.reduce((accumulator, currentValue) => {
        let value;
        if (count) {
            value = 1;
        } else {
            value = typeof currentValue[key] === 'string' ? parseFloat(currentValue[key]) : currentValue[key];
        }
        return accumulator + value;
    }, 0);
}

export function getNotEmptyValueCount(data, key) {
    let count = 0;

    data.forEach(obj => {
            if (obj.hasOwnProperty(key)) {
                const value = obj[key];
                
                // Check for non-empty strings, non-zero numbers, and non-null/undefined
                if((value !== null && value !== undefined)){
                    if (
                        obj['transactionType'] === 'Investment Sales' &&
                        ((typeof value === 'string' && value.trim() !== '') ||
                        (typeof value === 'number' && value !== 0))
                        ) {
                        count++;
                        }
                }    
            }
        });

  return count;
}

export function formatPhoneNumber(value) {
    let str = String(value).trim();
    if (!str) return '';
    // International: + followed by something other than 1 (e.g. +44, +33) → leave as is
    if (str.startsWith('+')) {
        const afterPlus = str.slice(1).trim();
        const firstChar = afterPlus.charAt(0);
        if (firstChar && firstChar !== '1') return str;
        str = str.slice(2);
    }
    // US path: strip non-digits, optionally remove leading 1
    let digits = str.replace(/\D/g, '');
    // A bare country code ("1-214-766-5283") is not part of the 10-digit number.
    if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
    const cvLength = digits.length;
    if (cvLength === 0) return '';
    if (cvLength <= 3) return `(${digits}`;
    if (cvLength <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

/**
 * Display-only phone formatting for stored values. Masks a complete US number
 * and leaves anything else ("0", "ext 299", a 9-digit number) exactly as
 * stored: formatPhoneNumber must keep returning partial masks because inputs
 * call it on every keystroke, and those masks turn junk data into numbers that
 * look real ("0" became "(0").
 */
export function formatStoredPhoneNumber(value) {
    const str = value === null || value === undefined ? '' : String(value).trim();
    const digits = str.replace(/\D/g, '');
    if (digits.length === 10 || (digits.length === 11 && digits.startsWith('1'))) {
        return formatPhoneNumber(str);
    }
    return str;
}

/**
 * Listing agent name for print proposal footers: title case (first letter of
 * each word), not all caps.
 */
export function formatProposalAgentDisplayName(firstName, lastName) {
    const titleWord = (w) => {
        if (!w) return '';
        return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    };
    const titlePart = (s) => {
        if (!s || !String(s).trim()) return '';
        return String(s).trim().split(/\s+/).map(titleWord).join(' ');
    };
    return [titlePart(firstName), titlePart(lastName)].filter(Boolean).join(' ');
}

/**
 * Listing agent email for proposal footers, e.g. AGottschalk@SummitRE.io —
 * first initial + capitalized last name; summitre.* domain becomes SummitRE.*.
 */
export function formatProposalListingEmail(email, firstName, lastName) {
    if (!email || typeof email !== 'string') return email || '';
    const trimmed = email.trim();
    const at = trimmed.indexOf('@');
    if (at === -1) return trimmed;

    const domainPart = trimmed.slice(at + 1).trim();
    const domainLower = domainPart.toLowerCase();

    let local;
    const fn = firstName && String(firstName).trim();
    const ln = lastName && String(lastName).trim();
    if (fn && ln) {
        const initial = fn.charAt(0).toUpperCase();
        const lastFormatted = ln.split('-').map((seg) => {
            const part = seg.trim();
            if (!part) return '';
            return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
        }).filter(Boolean).join('-');
        local = initial + lastFormatted;
    } else {
        const rawLocal = trimmed.slice(0, at);
        local = rawLocal.charAt(0).toUpperCase() + rawLocal.slice(1).toLowerCase();
    }

    let domainOut = domainPart;
    if (domainLower === 'summitre.io') {
        domainOut = 'SummitRE.io';
    } else if (domainLower === 'summitre.com') {
        domainOut = 'SummitRE.com';
    } else if (/^summitre\./i.test(domainPart)) {
        domainOut = domainPart.replace(/^summitre/i, 'SummitRE');
    }

    return `${local}@${domainOut}`;
}

export function formatNumber(value, isPrice, decimals) {
    if (!value) return value;
    let currentValue = value;
    if (typeof value !== 'string') currentValue = currentValue.toString();
    currentValue = currentValue.replace(/[^\d|.]/g, '');
    if (decimals) return parseFloat(currentValue).toLocaleString(undefined, {maximumFractionDigits: decimals, minimumFractionDigits: decimals});
    if (isPrice) return addCommasToNumberAsString(currentValue);
    return currentValue;
}

export function formatDate(value) {
    if (!value) return value;
    return value.replace(/[^\d|/]/g, '');
}

export function formatAgentPercent(value) {
    if (!value) return value;
    const number = value.split('.');
    const decimals = number[1] ? number[1].length : null;
    if (decimals && decimals > 15) return value.slice(0, -1);
    return value;
}

export function capitalize(string) {
    if (!string) {
        return '';
    }
    return string[0].toUpperCase() + string.slice(1);
}

// separated by comma
export function capitalizeAll(string) {
    if (!string) {
        return '';
    }
    let array = string.split(',');
    array = array.map(el => capitalize(el));
    return array.join(', ');
}

export function formatName(data, key = '') {
    // Check if there's a NAME field (new investor structure)
    if (data[`${key}NAME`] && data[`${key}NAME`].trim()) {
        return data[`${key}NAME`];
    }
    
    // Fallback to old structure for backward compatibility
    if (data[`${key}FIRST_NAME`] && data[`${key}LAST_NAME`] && data[`${key}FIRST_NAME`].trim() && data[`${key}LAST_NAME`].trim())
        return `${data[`${key}FIRST_NAME`]} ${data[`${key}LAST_NAME`]}`;
    else {
        if (data[`${key}FIRST_NAME`] && data[`${key}FIRST_NAME`].trim()) return data[`${key}FIRST_NAME`];
        if (data[`${key}LAST_NAME`] && data[`${key}LAST_NAME`].trim()) return data[`${key}LAST_NAME`];
        if (data[`${key}COMPANY_NAME`] && data[`${key}COMPANY_NAME`].trim()) return data[`${key}COMPANY_NAME`];
    }
    return null;
}

export function formatType(type) {
    if (!type)
        return null;
    if (type.includes('STNL') && type.includes('MT') && type.includes('Other'))
        return 'Single-Tenant, Other, Multi-Tenant';
    if (type.includes('STNL') && type.includes('MT'))
        return 'Single-Tenant, Multi-Tenant';
    if (type.includes('STNL') && type.includes('Other'))
        return 'Single-Tenant, Other';
    if (type.includes('MT') && type.includes('Other'))
        return 'Other, Multi-Tenant';
    if (type.includes('STNL'))
        return 'Single-Tenant';
    if (type.includes('MT'))
        return 'Multi-Tenant';
    if (type.includes('Other'))
        return 'Other';
    return null;
}

export function addSpaceAfterComma(string) {
    if (!string) return '';
    let result = cloneDeep(string);
    result = result.replace(/,/g, ', ');
    return result;
}

export function percent(value, decimals = 0, maxDecimals = 0) {
    if (value === undefined || value === null || value === '') return '';
    // maxDecimals shows the extra digits only where a rate actually carries
    // them, so a plain 40% still reads 40.00%.
    if (maxDecimals > decimals) {
        return Number(value).toLocaleString(undefined, {minimumFractionDigits: decimals, maximumFractionDigits: maxDecimals})+'%';
    }
    if (decimals) return Number(value).toFixed(decimals)+'%';
    return parseInt(value)+'%';
}

export function formatPropertyName(name, city, state) {
    if (city && state) return `${name} - ${city}, ${state}`;
    if (city) return `${name} - ${city}`;
    if (state) return `${name} - ${state}`;
    return name;
}

// array of objects
export function sortArray(array, key, desc = false) {
    const sortedArray = cloneDeep(array);
    if (key) {
        if (desc) {
            sortedArray.sort((a, b) => (a[key] > b[key]) ? -1 : 1);
        } else {
            sortedArray.sort((a, b) => (a[key] < b[key]) ? -1 : 1);
        }
    } else {
        if (desc) {
            sortedArray.sort((a, b) => (a > b) ? -1 : 1);
        } else {
            sortedArray.sort((a, b) => (a < b) ? -1 : 1);
        }
    }
    return sortedArray;
}

export function formatAddress(address) {
    if (address!= null && address.includes('Farm to Market')) {
        address = address.replaceAll('Farm to Market', 'FM');
    }
    if (address!= null && address.includes('Parkway')) {
        address = address.replaceAll('Parkway', 'Pkwy');
    }
    if (address!= null && address.includes('Farm Road')) {
        address = address.replaceAll('Farm Road', 'FM');
    }
    if (address!= null && address.includes('Road')) {
        address = address.replaceAll('Road', 'Rd');
    }
    if (address!= null && address.includes('Avenue')) {
        address = address.replaceAll('Avenue', 'Ave');
    }
    if (address!= null && address.includes('Boulevard')) {
        address = address.replaceAll('Boulevard', 'Blvd');
    }
    if (address!= null && address.includes('Circle')) {
        address = address.replaceAll('Circle', 'Cir');
    }
    if (address!= null && address.includes('Court')) {
        address = address.replaceAll('Court', 'Ct');
    }
    if (address!= null && address.includes('Street')) {
        address = address.replaceAll('Street', 'St');
    }
    if (address!= null && address.includes('Lane')) {
        address = address.replaceAll('Lane', 'Ln');
    }
    if (address!= null && address.includes('Highway')) {
        address = address.replaceAll('Highway', 'Hwy');
    }
    if (address!= null && address.includes('Expressway')) {
        address = address.replaceAll('Expressway', 'Expy');
    }
    if (address!= null && address.includes('Farm-to-Market') || address!= null && address.includes('Farm to Market')) {
        address = address.replaceAll(/Farm-to-Market|Farm to Market/, 'FM');
    }
    if (address!= null && address.includes('Post Office')) {
        address = address.replaceAll('Post Office', 'PO');
    }
    if (address!= null && address.match(/I\s\d+|I\s-\s\d+/g)) {
        address = address.replaceAll(/I\s-\s|I\s/g, 'I-');
    }
    if (address!= null && address.includes('INTERSTATE')) {
        address = address.replaceAll('INTERSTATE', 'Interstate');
    }
    if (address!= null && address.match(/Interstate\s\d+|Interstate\s-\s\d+|Interstate-\d+/g)) {
        address = address.replaceAll(/Interstate\s-\s|Interstate\s|Interstate-/g, 'I-');
    }
    return address;
}

export function renderPrintFooter(isPrint, isHSC = false) {
    if (!isPrint) {
        return null;
    }
    return (
        <div className={`print-footer ${isHSC ? 'hsc' : ''}`}>
            <div className='logo'>
                <img src={arc.path.media + (isHSC ? hscLogo : summitLogo)} alt=''/>
            </div>
            {
                isHSC ? 'summitre.com' : null
            }
        </div>
    )
}

export function getWeeks(startDate, endDate) {
    const dayToMs = 1000 * 60 * 60 * 24;
    const days = (endDate - startDate) / dayToMs;
    return Math.floor((days + startDate.getDay()+1) / 7);
}

// for financials tenants
export function findTenantRow(rows, valueKey, value) {
    if (!rows) {
        return;
    }
    const keys = Object.keys(rows);
    for (let index = 0; index < keys.length; index++) {
        const row = rows[keys[index]];
        const cell = row && row.valueCells && row.valueCells[valueKey];
        if (cell && cell.value === value) return row;
    }
}

export function formatPipelineTitle(value) {
    if (!value) return;
    if(value == 'total-closed') return "Total Closed";
    let title = value.split('-');
    if (!title[1]) return title[0][0].toUpperCase()+title[0].slice(1);
    return `${title[0][0].toUpperCase()+title[0].slice(1)} ${title[1].toUpperCase()}`;
}

export function getTenantSubcategorySTNL({data, handleChangeDropdown, viewMode=false, deleted=false, theme, className, isFromProperty=false, disabled=false, isFilter=false}) {
    switch (data.CATEGORY){
        case "office":
            return <Dropdown
                theme={theme}
                className={className}
                data={data}
                isFromProperty={isFromProperty}
                optionsType="singleTenantOffice"
                value={data.SINGLE_TENANT_SUBCATEGORY}
                valueKey="SINGLE_TENANT_SUBCATEGORY"
                label="Tenant Subcategory"
                handleParentChange={handleChangeDropdown}
                viewMode={viewMode}
                deleted={deleted}
                disabled={disabled}
                isFilter={isFilter}
            />
        case "industrial":
            return <Dropdown
                theme={theme}
                className={className}
                data={data} 
                isFromProperty={isFromProperty}
                optionsType="subcategoryIndustrialSTNLMT"
                value={data.SINGLE_TENANT_SUBCATEGORY}
                valueKey="SINGLE_TENANT_SUBCATEGORY"
                label="Tenant Subcategory"
                handleParentChange={handleChangeDropdown}
                viewMode={viewMode}
                deleted={deleted}
                disabled={disabled}
                isFilter={isFilter}
            />
        default :
            return <Dropdown
                theme={theme}
                className={className}
                data={data}
                isFromProperty={isFromProperty}
                optionsType="subcategorySTNL"
                value={data.SINGLE_TENANT_SUBCATEGORY}
                valueKey="SINGLE_TENANT_SUBCATEGORY"
                label="Tenant Subcategory"
                handleParentChange={handleChangeDropdown}
                viewMode={viewMode}
                deleted={deleted}
                disabled={disabled}
                isFilter={isFilter}
            />
    }
}

export function getTenantSubcategoryMT({data, handleChangeDropdown, viewMode=false, deleted=false, theme, className, isFromProperty=false, disabled=false, isFilter=false}) {
    switch (data.CATEGORY){
        case "office":
            return <Dropdown
                theme={theme}
                className={className}
                data={data}   
                isFromProperty={isFromProperty}
                optionsType="multiTenantOffice"
                value={data.MULTI_TENANT_SUBCATEGORY}
                valueKey="MULTI_TENANT_SUBCATEGORY"
                label="Tenant Subcategory"
                handleParentChange={handleChangeDropdown}
                viewMode={viewMode}
                deleted={deleted}
                disabled={disabled}
                isFilter={isFilter}
            />
        case "industrial":
            return <Dropdown
                theme={theme}
                className={className}
                data={data}
                isFromProperty={isFromProperty}
                optionsType="subcategoryIndustrialSTNLMT"
                value={data.MULTI_TENANT_SUBCATEGORY}
                valueKey="MULTI_TENANT_SUBCATEGORY"
                label="Tenant Subcategory"
                handleParentChange={handleChangeDropdown}
                viewMode={viewMode}
                deleted={deleted}
                disabled={disabled}
                isFilter={isFilter}
            />
        default :
            return <Dropdown
                theme={theme}
                className={className}
                data={data}
                isFromProperty={isFromProperty}
                optionsType="subcategoryMT"
                value={data.MULTI_TENANT_SUBCATEGORY}
                valueKey="MULTI_TENANT_SUBCATEGORY"
                label="Tenant Subcategory"
                handleParentChange={handleChangeDropdown}
                viewMode={viewMode}
                deleted={deleted}
                disabled={disabled}
                isFilter={isFilter}
            />
    }
}

/*  Net Commission = 50% of Gross Commission
    55% on any gross commissions over $200,001
    It continues every $200,000 and tops out at 70%
 */
export function getPaymentPercentage(grossCumulative) {
    if (isNaN(grossCumulative))
        return 0;

    let modifier = Math.floor(grossCumulative / 200001); 
    if(modifier < 0){
        modifier = 0;
    }
    if (modifier > 4)
        return 50 + 5 * 4;
    return 50 + 5 * modifier;
}

export function getPaymentThreshold(grossCumulative) {
    if (grossCumulative > 200000 && grossCumulative <= 400000)
        return 200000;
    if (grossCumulative > 400000 && grossCumulative <= 600000)
        return 400000;
    if (grossCumulative > 600000 && grossCumulative <= 800000)
        return 600000;
    if (grossCumulative > 800000)
        return 800000;
    return 0;
}
export function getCurrentTrancheOnGrossAmount(grossCumulative) {
    if (grossCumulative <= 200000  ) {
        return 0.50;                            
    }else if(grossCumulative>200000 && grossCumulative<=400000 ){
        return 0.55;                             
    }else if(grossCumulative>400000 && grossCumulative<=600000 ){
        return 0.60;                
    }else if(grossCumulative>600000 && grossCumulative<=800000 ){
        return 0.65;                               
    }else if(grossCumulative>800000 ){
        return 0.70;                               
    }
    return 0;
}

export function getAgentContribution(property, agentId) {
    const agents = {};

    // Listing agents
    if (property.LISTING_AGENT_1 && property.LISTING_AGENT_1_PERCENT) {
        agents[property.LISTING_AGENT_1] = parseFloat(property.LISTING_AGENT_1_PERCENT) || 0;
    }
    if (property.LISTING_AGENT_2 && property.LISTING_AGENT_2_PERCENT) {
        agents[property.LISTING_AGENT_2] = (agents[property.LISTING_AGENT_2] || 0) + parseFloat(property.LISTING_AGENT_2_PERCENT) || 0;
    }
    if (property.LISTING_AGENT_3 && property.LISTING_AGENT_3_PERCENT) {
        agents[property.LISTING_AGENT_3] = (agents[property.LISTING_AGENT_3] || 0) + parseFloat(property.LISTING_AGENT_3_PERCENT) || 0;
    }

    // Buyer agents
    if (property.BUYER_AGENT_1 && property.BUYER_AGENT_1_PERCENT) {
        agents[property.BUYER_AGENT_1] = (agents[property.BUYER_AGENT_1] || 0) + parseFloat(property.BUYER_AGENT_1_PERCENT) || 0;
    }
    if (property.BUYER_AGENT_2 && property.BUYER_AGENT_2_PERCENT) {
        agents[property.BUYER_AGENT_2] = (agents[property.BUYER_AGENT_2] || 0) + parseFloat(property.BUYER_AGENT_2_PERCENT) || 0;
    }
    if (property.BUYER_AGENT_3 && property.BUYER_AGENT_3_PERCENT) {
        agents[property.BUYER_AGENT_3] = (agents[property.BUYER_AGENT_3] || 0) + parseFloat(property.BUYER_AGENT_3_PERCENT) || 0;
    }
       
    const totalContributions = Object.values(agents).reduce((sum, value) => sum + value, 0);    
    const agentContribution = agents[agentId] || 0;
    return (agentContribution / totalContributions) * 100;
}
export function insertCurrentDataToSortedArray(currentData, sortedArray, filters, weightsArray){        
    let insertIndex = 0, done = false, nextFilter, j = 0;

    //ordered filters
    filters = filters.sort((a, b) => weightsArray.filter(el => el.key === a.split('.')[0])[0].weight > weightsArray.filter(el => el.key === b.split('.')[0])[0].weight ? 1 : -1)
    if(filters.length>0)
        for(let i = 0; i< sortedArray.length && !done; i++) {
            nextFilter=false; j=0;
            while(!nextFilter && !done && j<filters.length) {
                const filter = filters[j].split('.')[0];
                const direction = filters[j].split('.')[1];

                if(currentData[filter]!=null){
                    if(direction === 'asc' && currentData[filter].localeCompare(sortedArray[i][filter], 'en',{numeric: true}) < 0){
                        if(!sortedArray[i][filter])
                            nextFilter = true;
                        else {
                            insertIndex = i;
                            done = true;
                        }
                    }
                    else if(direction === 'desc' && currentData[filter].localeCompare(sortedArray[i][filter], 'en', {numeric: true}) > 0){
                        insertIndex = i;
                        done = true;
                    }
                    else if(direction === 'desc' && currentData[filter].localeCompare(sortedArray[i][filter], 'en', {numeric: true}) < 0 || 
                            direction === 'asc' && currentData[filter].localeCompare(sortedArray[i][filter], 'en', {numeric: true}) > 0){
                        if(!sortedArray[i][filter])
                            done = true;
                        else {
                            insertIndex = i;
                            nextFilter = true;
                        }
                    }
                    else if(currentData[filter].localeCompare(sortedArray[i][filter], 'en', {numeric: true}) === 0 ){
                        insertIndex = i;
                        j++;
                    }
                }
                else{
                    if(direction === 'asc'){
                        insertIndex = i;
                        done=true;
                    } else if(direction === 'desc'){
                        insertIndex = i;
                        nextFilter=true;
                    }
                }
            }
        }
    
    if(!done) {
        insertIndex++;
    } 
    
    sortedArray.splice(insertIndex, 0, currentData);
    return sortedArray;
}

export function getBase64(file) {
    return new Promise(resolve => {
        let baseURL = "";
        let reader = new FileReader();

        reader.readAsDataURL(file);
        reader.onload = () => {
            baseURL = reader.result;
            resolve(baseURL);
        };
    });
}

export function getHSCCumulativeAgentPercent(grossCumulative) {
    if (grossCumulative >= 0 && grossCumulative <= 250000)
        return 0.5;
    if (grossCumulative > 250000 && grossCumulative <= 500000)
        return 0.55;
    if (grossCumulative > 500000 && grossCumulative <= 750000)
        return 0.6;
    if (grossCumulative > 750000 && grossCumulative <= 1000000)
        return 0.65;
    if (grossCumulative > 1000000)
        return 0.7;
    return 0;
}

export function getHSCCumulativeThreshold(grossCumulative) {
    if (grossCumulative > 250000 && grossCumulative <= 500000)
        return 250000;
    if (grossCumulative > 500000 && grossCumulative <= 750000)
        return 500000;
    if (grossCumulative > 750000 && grossCumulative <= 1000000)
        return 750000;
    if (grossCumulative > 1000000)
        return 1000000;
    return 0;
}
export function removeDuplicateObjects(arr){
    const seen = new Set();
    return arr.filter(obj => {
        const stringfiedObj = JSON.stringify(obj);
        if(!seen.has(stringfiedObj)) {
            seen.add(stringfiedObj);
            return true;
        }
        return false;
    });
}
export function getAgentPercentage(property,agentId){
    let buyerAgent;
    let listingAgent;
    let percentage;
    const buyerAgents = [];
    const listingAgents = [];

    property.BUYER_AGENT_1 !== null ? buyerAgents.push({"agent":property.BUYER_AGENT_1,"agentPercentage":parseFloat(property.BUYER_AGENT_1_PERCENT || 0)}):null;
    property.BUYER_AGENT_2 !== null ? buyerAgents.push({"agent":property.BUYER_AGENT_2,"agentPercentage":parseFloat(property.BUYER_AGENT_2_PERCENT || 0)}):null;
    property.BUYER_AGENT_3 !== null ? buyerAgents.push({"agent":property.BUYER_AGENT_3,"agentPercentage":parseFloat(property.BUYER_AGENT_3_PERCENT || 0)}):null;
    /* if (property.BUYER_AGENT_1 && parseFloat(property.BUYER_AGENT_1_PERCENT || 0) !== 0) {
        listingAgents.push({
            agent: property.BUYER_AGENT_1,
            agentPercentage: parseFloat(property.BUYER_AGENT_1_PERCENT)
        });
    }
    if (property.BUYER_AGENT_2 && parseFloat(property.BUYER_AGENT_2_PERCENT || 0) !== 0) {
        listingAgents.push({
            agent: property.BUYER_AGENT_2,
            agentPercentage: parseFloat(property.BUYER_AGENT_2_PERCENT)
        });
    }
    if (property.BUYER_AGENT_3 && parseFloat(property.BUYER_AGENT_3_PERCENT || 0) !== 0) {
        listingAgents.push({
            agent: property.BUYER_AGENT_3,
            agentPercentage: parseFloat(property.BUYER_AGENT_3_PERCENT)
        });
    }
    if (property.LISTING_AGENT_1 && parseFloat(property.LISTING_AGENT_1_PERCENT || 0) !== 0) {
        listingAgents.push({
            agent: property.LISTING_AGENT_1,
            agentPercentage: parseFloat(property.LISTING_AGENT_1_PERCENT)
        });
    }
    if (property.LISTING_AGENT_2 && parseFloat(property.LISTING_AGENT_2_PERCENT || 0) !== 0) {
        listingAgents.push({
            agent: property.LISTING_AGENT_2,
            agentPercentage: parseFloat(property.LISTING_AGENT_2_PERCENT)
        });
    }
    if (property.LISTING_AGENT_3 && parseFloat(property.LISTING_AGENT_3_PERCENT || 0) !== 0) {
        listingAgents.push({
            agent: property.LISTING_AGENT_3,
            agentPercentage: parseFloat(property.LISTING_AGENT_3_PERCENT)
        });
    } */

    property.LISTING_AGENT_1 !== null ? listingAgents.push({"agent":property.LISTING_AGENT_1,"agentPercentage":parseFloat(property.LISTING_AGENT_1_PERCENT || 0)}):null;
    property.LISTING_AGENT_2 !== null ? listingAgents.push({"agent":property.LISTING_AGENT_2,"agentPercentage":parseFloat(property.LISTING_AGENT_2_PERCENT || 0)}):null;
    property.LISTING_AGENT_3 !== null ? listingAgents.push({"agent":property.LISTING_AGENT_3,"agentPercentage":parseFloat(property.LISTING_AGENT_3_PERCENT || 0)}):null;
      
    let countListingAgents = listingAgents.filter(item => item.agent === undefined).length;
    let countBuyerAgents = buyerAgents.filter(item => item.agent === undefined).length;    
    let buyerAgentsTotalPercentage = sumByPropertyName(buyerAgents,'agentPercentage');    
    let listingAgentsTotalPercentage = sumByPropertyName(listingAgents,'agentPercentage');     
    let totalAgentPercentage  = parseFloat(buyerAgentsTotalPercentage) + parseFloat(listingAgentsTotalPercentage);

    if(Math.floor(totalAgentPercentage) === 100 || property.TRANSACTION_TYPE === "Referral"){ 
        buyerAgent = buyerAgents.find(obj => obj.agent.toString() === agentId.toString());
        listingAgent = listingAgents.find(obj => obj.agent.toString() === agentId.toString());        
        percentage = parseFloat(buyerAgent? parseFloat(buyerAgent.agentPercentage):0) + parseFloat(listingAgent?parseFloat(listingAgent.agentPercentage):0);
        
     }else{
        const mergeAgents = buyerAgents.length !== 0 ?buyerAgents.map(obj1 => {
            const matchingObj = listingAgents.find(obj2 => obj2.agent === obj1.agent);
            return matchingObj ? {...obj1,...matchingObj}:obj1;
        }):listingAgents;        
        if(countListingAgents === 3 && countBuyerAgents === 3){
            percentage = 0;
        }else{
            let filteredAgents = mergeAgents.filter(agent => agent.agentPercentage !== 0);
            const agent = filteredAgents.find(propAgent => propAgent.agent.toString() === agentId.toString());    
            percentage = agent? 100/filteredAgents.length : 0;
        }
         
    } 
    return percentage/100;
    
}
export function sumByPropertyName(arr,propertyName){
    if(arr.length === 0 || !propertyName){
        return 0;
    }
    const sum = arr.reduce((accumulator,currentValue) => {
        if(currentValue.hasOwnProperty(propertyName) && typeof currentValue[propertyName] === 'number'){
            return accumulator + currentValue[propertyName];
        }else{
            return accumulator;
        }
    },0);
    return sum;
}

export function agentHasCommission(agent) {
    return (parseFloat(agent.LISTING_PROCURED_GROSS_FEE) || 0) !== 0 ||
        (parseFloat(agent.BUYING_PROCURED_GROSS_FEE) || 0) !== 0 ||
        (parseFloat(agent.PAID_GROSS_FEE) || 0) !== 0;
}

export function filterAgentsByDate(agents, givenYear) {
    return agents.filter(agent => {
        // The date window only trims zero-activity roster rows: an agent who
        // procured or was paid in the year keeps their row regardless of
        // employment dates (terminated agents can still be paid, and some
        // agents have no starting date on file).
        if (agentHasCommission(agent)) {
            return true;
        }

        const doomsday = 9999;
        const joiningYear = agent.AGENT_STARTING_DATE ? new Date(agent.AGENT_STARTING_DATE).getFullYear() : doomsday;
        const terminationYear = agent.AGENT_TERMINATION_DATE ? new Date(agent.AGENT_TERMINATION_DATE).getFullYear() : doomsday;

        return givenYear >= joiningYear && givenYear <=terminationYear;
    })
}

export function filterAgentsWithoutCommisionInTerminationYear(agents, givenYear) {
    let filteredAgents = []

  for (const agent of agents) {
    const hasZeroFees = (
      parseFloat(agent["BUYING_PROCURED_GROSS_FEE"]) === 0.0 &&
      parseFloat(agent["LISTING_PROCURED_GROSS_FEE"]) === 0.0 &&
      ( !agent["PAID_GROSS_FEE"]  || parseFloat(agent["PAID_GROSS_FEE"]) === 0.0)
    );
    const terminationYear = agent.AGENT_TERMINATION_DATE ? new Date(agent.AGENT_TERMINATION_DATE).getFullYear() : false;

    if (!hasZeroFees || !terminationYear) {
      filteredAgents.push(agent);
    }
  }
  return filteredAgents;

}



export function calculateYearsAndMonths(givenDate) {
    var currentDate = new Date(); // Current date
    
    // Calculate the difference in years
    var yearsDiff = currentDate.getFullYear() - givenDate.getFullYear();
    
    // Calculate the difference in months
    var monthsDiff = currentDate.getMonth() - givenDate.getMonth();

    if (currentDate.getDate() < givenDate.getDate()) {
        monthsDiff--;
    }
    
    // Adjust months difference if it's negative
    if (monthsDiff < 0) {
        yearsDiff--; // Subtract 1 year
        monthsDiff += 12; // Add 12 months
    }

    if (Number.isNaN(yearsDiff) || Number.isNaN(monthsDiff)) {
        return ''; 
    }


    if ((yearsDiff < 0)||(yearsDiff === 0 && monthsDiff === 0)) {
        return '0M';
    }

    if (yearsDiff === 0 && monthsDiff > 0) {
        return `${monthsDiff}M`;
    }

    return `${yearsDiff}Y ${monthsDiff}M`;
  }
export function calculateNextBussinessDay(startingDateString,daysAfter){
      // Input starting date string
      //var startingDateString = "2014-06-19";
  
      // Create a Date object with the starting date string
      var startingDate = new Date(startingDateString);
  
      // Add 90 days to the starting date
      var endDate = new Date(startingDate.getTime() + (daysAfter * 24 * 60 * 60 * 1000));
  
      // Check if the 90th day is a Saturday (6) or Sunday (0)
      if (endDate.getDay() === 6) { // Saturday
          endDate.setDate(endDate.getDate() + 2); // Move to Monday
      } else if (endDate.getDay() === 0) { // Sunday
          endDate.setDate(endDate.getDate() + 1); // Move to Monday
      }
  
      // Extract day, month, and year from the end date
      var day = endDate.getDate();
      var month = endDate.getMonth() + 1; // Months are zero-based
      var year = endDate.getFullYear();
  
      // Pad single-digit day and month with leading zeros if necessary
      day = day < 10 ? '0' + day : day;
      month = month < 10 ? '0' + month : month;
  
      // Form the MM/DD/YYYY format string for the end date
      var formattedEndDate = Number.isNaN(month) && Number.isNaN(day) && Number.isNaN(year)?'': month + '/' + day + '/' + year;
  
      return formattedEndDate;
  }
export function convertDateFormat(dateString){
   
      // Create a Date object with the input date string
      var date = new Date(dateString);
  
      // Extract day, month, and year from the Date object
      var day = date.getDate();
      var month = date.getMonth() + 1; // Months are zero-based
      var year = date.getFullYear();
  
      // Pad single-digit day and month with leading zeros if necessary
      day = day < 10 ? '0' + day : day;
      month = month < 10 ? '0' + month : month;
  
      // Form the MM/DD/YYYY format string
      var formattedDate = month + '/' + day + '/' + year;
      return formattedDate;
  
  }
  export function convertDateFormatYYYYMMDD(dateString) {
    // Already YYYY-MM-DD (e.g. re-converting a value saveData() already
    // normalized) — return as-is instead of falling through to the '/' split
    // below, which would blow up and silently return '' (Claritask: wiped
    // Start Date/Birth Date on team members after saving a termination date).
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
    }

    try {
         // Split the date string into components
        let parts = dateString.split('/');

        // Reorder the components to YYYY-MM-DD format
        let convertedDate = parts[2] + '-' + parts[0].padStart(2, '0') + '-' + parts[1].padStart(2, '0');

        return convertedDate;
    } catch (error) {
        return '';
    }
}
export function formattedDate(dateString) {
    if (!dateString) return null;

    // The API may send a full ISO datetime ("1976-02-11T00:00:00.000000Z") or a
    // "YYYY-MM-DD HH:MM:SS" string — keep just the date portion before matching.
    let datePart = String(dateString).trim().split(/[ T]/)[0];

    // Regular expression to match YYYY-MM-DD format
    let yyyy_mm_dd_regex = /^\d{4}-\d{2}-\d{2}$/;

    // Regular expression to match MM/DD/YYYY format
    let mm_dd_yyyy_regex = /^\d{2}\/\d{2}\/\d{4}$/;

    // Check if the date string matches either format
    if (yyyy_mm_dd_regex.test(datePart)) {
        // If it's in YYYY-MM-DD format, convert it to MM/DD/YYYY format
        let parts = datePart.split('-');
        return parts[1] + '/' + parts[2] + '/' + parts[0];
    } else if (mm_dd_yyyy_regex.test(datePart)) {
        // If it's already in MM/DD/YYYY format, return it as is
        return datePart;
    } else {
        // If it doesn't match either format, return null or handle the error accordingly
        return null;
    }
}

export function isGivenDateInCurrentYear(dateString) {
    // Parse the given date string into a Date object
    if(!dateString){
        return false;
    }
    const parts = dateString.split('/');
    const year = parseInt(parts[2], 10);
    const month = parseInt(parts[0], 10) - 1; // Month is 0-based
    const day = parseInt(parts[1], 10);
    const dateObject = new Date(year, month, day);

    // Get the current year
    const currentYear = new Date().getFullYear();
    const isInCurrentYear = dateObject.getFullYear() >= currentYear;  
    return isInCurrentYear;
}
export function getCompanyName(email) {  
    const parts = email.split('@');
    if (parts.length === 2) { 
        const domain = parts[1];
        const companyName = domain.split('.')[0]; 
        return companyName.toLowerCase();
    } else {
        return null; 
    }
}
/**
 * Whether a member's Forms uploads are disabled for the viewer: open when
 * they may change everyone's Forms (team.forms = all), or for their own.
 */
export function checkUploadAccess(loggedInUserEmail, selectedTeamMemberEmail, mayChangeEveryonesForms = false) {
    if (mayChangeEveryonesForms) {
        return false; // Not disabled = uploads enabled
    }
    
    // Users can upload for their own profile
    if (loggedInUserEmail.toLowerCase() === (selectedTeamMemberEmail && selectedTeamMemberEmail.toLowerCase())) {
        return false; // Not disabled = uploads enabled
    }
    
    // Everyone else: uploads disabled
    return true; // Disabled = uploads not allowed
}
export function isValidDate(date) {
    return date instanceof Date && !isNaN(date);
}
export function formatDateString(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based, so add 1
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    //return `${month}/${day}/${year} ${hours}:${minutes}`;
    return `${year}-${month}-${day} ${hours}:${minutes}`;
}
export function formatDateTime(input) {  
    if (!input) {
        return '';
    }
    // Check if input is an ISO 8601 date string
    if (input.includes('T') && input.includes('Z')) {
        const isoDate = new Date(input);
        if (isValidDate(isoDate)) {
            return formatDateString(isoDate);
        }
    }
    // Check if input is in ISO 8601 format without milliseconds
    const isoWithoutMsRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;
    if (isoWithoutMsRegex.test(input)) {
        const isoDate = new Date(input);
        if (isValidDate(isoDate)) {
            return formatDateString(isoDate);
        }
    }
    // Check if input is in the format YYYY-MM-DD HH:mm:ss
    const yyyymmddhhmmssRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
    if (yyyymmddhhmmssRegex.test(input)) {
        const date = new Date(input.replace(' ', 'T'));
        if (isValidDate(date)) {
            return formatDateString(date);
        }
    }

    // Check if input is in the format MM/DD/YYYY
    const mmddyyyyRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    if (mmddyyyyRegex.test(input)) {
        const dateParts = input.split('/');
        const month = parseInt(dateParts[0], 10);
        const day = parseInt(dateParts[1], 10);
        const year = parseInt(dateParts[2], 10);
        const formattedDate = new Date(`${year}-${month}-${day}`);
        if (isValidDate(formattedDate)) {
            return formatDateString(formattedDate);
        }
    }
    const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
    // Check if the input matches the ISO format
    if (isoRegex.test(input)) {
        // Convert the ISO format to the desired format
        return input.replace('T', ' ');
    } 

    const dateTimeRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/;
    // Check if the input matches the specified format
    if (dateTimeRegex.test(input)) {
        //const [date, time] = input.split(' ');    
        const dateTime = input.split(' ');        
        const date = dateTime[0];
        const time = dateTime[1];    
        
        const tempDate = date.split('-');
        const year = tempDate[0];
        const month = tempDate[1];
        const day = tempDate[2];
        //const formattedDate = `${month}/${day}/${year}`;
        //const formattedDate = `${day}-${month}-${year}`;
        const formattedDate = `${year}-${month}-${day}`;
        return `${formattedDate} ${time}`;        
        //return input;
    } 

    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    // Check if the input matches the date format
    if (dateRegex.test(input)) {
        // Append '00:00' to the date
        return `${input} 00:00`;
    }

    return 'invalid';
}

export function viewDateTimeFormat(input) {
    console.log('[viewDateTimeFormat] Close Of Escrow input:', input);
    if (!input) return 'invalid format';

    // Normalize T separator to space (datetime-local inputs use T)
    const normalized = input.replace('T', ' ').replace('Z', '').trim();

    const datePattern = /^\d{4}-\d{2}-\d{2}$/;
    if (datePattern.test(normalized)) {
        const [year, month, day] = normalized.split('-');
        return `${month}/${day}/${year}`;
    }

    // Match YYYY-MM-DD HH:MM or YYYY-MM-DD HH:MM:SS (with optional milliseconds)
    const dateTimeRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

    if (dateTimeRegex.test(normalized)) {
        const [date] = normalized.split(' ');
        const [year, month, day] = date.split('-');
        return `${month}/${day}/${year}`;
    }

    // Match M/DD/YY HH:MM or MM/DD/YYYY HH:MM (US date format with time)
    const usDateTimeRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})\s+\d{1,2}:\d{2}$/;
    const usMatch = normalized.match(usDateTimeRegex);
    if (usMatch) {
        const month = usMatch[1].padStart(2, '0');
        const day = usMatch[2].padStart(2, '0');
        let year = usMatch[3];
        if (year.length === 2) year = '20' + year;
        return `${month}/${day}/${year}`;
    }

    // Match M/DD/YY or MM/DD/YYYY (US date format without time)
    const usDateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/;
    const usDateMatch = normalized.match(usDateRegex);
    if (usDateMatch) {
        const month = usDateMatch[1].padStart(2, '0');
        const day = usDateMatch[2].padStart(2, '0');
        let year = usDateMatch[3];
        if (year.length === 2) year = '20' + year;
        return `${month}/${day}/${year}`;
    }

    return 'invalid format';
}

export function convertDateTimeFormatToDate(input) {
    const datePattern = /^\d{4}-\d{2}-\d{2}$/;
    if (datePattern.test(input)) {
        const [year, month, day] = input.split('-');
        return `${month}/${day}/${year}`;
    }

    // Match YYYY-MM-DD HH:MM or YYYY-MM-DD HH:MM:SS
    const dateTimeRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/;

    if (dateTimeRegex.test(input)) {
        const [date,time] = input.split(' ');
        const [year, month, day] = date.split('-');
        const formattedDate = `${month}/${day}/${year}`;
        return `${formattedDate}`;
    }
}

export function formatDateTimeString(dateTimeString) {
    if (isValidDateTimeFormat(dateTimeString)) {
        const datePart = dateTimeString.split(' ')[0];
        return datePart;
    } else {
        return dateTimeString;
    }
}
export function isValidDateTimeFormat(dateTimeString) {
    // Regular expression to match the YYYY-MM-DD HH:MM format
    const regex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])\s([01]\d|2[0-3]):([0-5]\d)$/;
    if (!regex.test(dateTimeString)) {
        return false;
    }
    const [datePart, timePart] = dateTimeString.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);

    const date = new Date(year, month - 1, day, hour, minute);

    return date.getFullYear() === year && date.getMonth() + 1 === month && date.getDate() === day &&
           date.getHours() === hour && date.getMinutes() === minute;
}


export function isValidPriceChange(value="") {
    if(typeof value === 'number' && !isNaN(value)){
        return value.toFixed(2) +"%";
    }
    return "-";
}

export function compareDates(a,b){
    const dateA = new Date(a.CLOSE_OF_ESCROW);
    const dateB = new Date(b.CLOSE_OF_ESCROW);
    return dateA - dateB;
}

export function addDaysSkippingWeekends(startDate, daysToAdd) {
    let currentDate = moment(startDate);
    let addedDays = 0;

    while (addedDays < daysToAdd) {
        currentDate = currentDate.add(1, 'days'); // Add one day at a time

        // Skip if the current day is Saturday (6) or Sunday (7)
        if (currentDate.isoWeekday() !== 6 && currentDate.isoWeekday() !== 7) {
            addedDays++;
        }
    }

    return currentDate;
}

export function numberToWords(num = 0) {
    const a = [
        '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 
        'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 
        'Eighteen', 'Nineteen'
    ];
    const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const units = ['', 'Thousand', 'Million', 'Billion', 'Trillion'];

    if (num === 0) return 'Zero Dollars';

    const [integerPart, decimalPart] = num.toString().split('.'); // Split decimal parts for calculating cents

    let words = '';
    let unitIndex = 0;
    let intNum = parseInt(integerPart, 10);

    // Process integer part
    while (intNum > 0) {
        let chunk = intNum % 1000;
        if (chunk > 0) {
            let chunkWords = '';
            if (chunk > 99) {
                chunkWords += a[Math.floor(chunk / 100)] + ' Hundred ';
                chunk %= 100;
            }
            if (chunk > 19) {
                chunkWords += b[Math.floor(chunk / 10)] + ' ';
                chunk %= 10;
            }
            if (chunk > 0) {
                chunkWords += a[chunk] + ' ';
            }
            words = chunkWords + units[unitIndex] + ' ' + words;
        }
        intNum = Math.floor(intNum / 1000);
        unitIndex++;
    }

    words = words.trim() + ' Dollars';

    if (decimalPart) {
        let decimalWords = '';
        for (let i = 0; i < decimalPart.length; i++) {
            decimalWords += a[parseInt(decimalPart[i], 10)] + ' ';
        }
        words += ' and ' + decimalWords.trim() + ' Cents';
    }

    return words;
}

export function convertToWords(value='') {
    const num = parseInt(value.replace(` Days`, ''), 10);
    const ones = ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];
    const teens = ["", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"];
    const tens = ["", "ten", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];

    if (num === 0) return "zero";
    if (num < 10) return ones[num];
    if (num > 10 && num < 20) return teens[num - 10];
    if (num % 10 === 0) return tens[Math.floor(num / 10)];

    return tens[Math.floor(num / 10)] + " " + ones[num % 10];
}

/**
 * The rows that make a loan quote a quote. Purchase Price, Cap Rate, NOI, NOI
 * Percentage and Total Origination Fee are left out on purpose: the tab fills
 * those in from the property, so they are present even on a column nobody has
 * touched.
 */
const LOAN_QUOTE_CONTENT = [
    'DOWN_PAYMENT_PERCENT', 'DOWN_PAYMENT', 'LOAN_AMOUNT', 'INTEREST_RATE',
    'LOAN_TERM', 'MATURITY', 'AMORTIZATION', 'PREPAYMENT_PENALTY', 'RECOURSE',
    'LOAN_PAYMENT_ANNUALLY', 'LOAN_PAYMENT_MONTHLY', 'CASH_FLOW_ANNUALLY',
    'CASH_FLOW_MONTHLY', 'CASH_ON_CASH', 'DSCR', 'DEBT_YIELD', 'NOTES',
    'BANK', 'BANK_CONTACT', 'APPROVED',
];

/**
 * Whether a loan quote column holds an option: something has been filled in on
 * it. A column nobody has typed into is not one — not when it has been ticked
 * for print, and not when a saved-but-empty row is behind it. Those get no
 * number, are not ticked by Select All, do not print, and are never inserted.
 */
export function isLoanOption(column) {
    if (!column) {
        return false;
    }

    return LOAN_QUOTE_CONTENT.some((key) => {
        const value = column[key];

        return value !== null && value !== undefined && value !== '';
    });
}

/** What the Loan Structure row calls a quote: which option it is. */
export function loanOptionLabel(number) {
    return `Loan Option #${number}`;
}

/**
 * Name each loan quote column after the option it is — "Loan Option #1", "#2" —
 * counting only the columns that hold an option, so the numbers run 1..N with no
 * gaps whatever blank columns sit among them.
 */
export function withLoanOptionLabels(columns) {
    let optionNumber = 0;

    return (columns || []).map((column) => {
        if (!isLoanOption(column)) {
            return column;
        }

        optionNumber += 1;

        return { ...column, LOAN_STRUCTURE: loanOptionLabel(optionNumber) };
    });
}

/**
 * What the printed Loan Quotes PDF is saved as:
 *
 *   Peak Capital Loan Quotes - Cedar Hill Crossing - Cedar Hill, TX 8.20.26
 *
 * The middle is the title already printed at the top of the page, and the date
 * is the day it is printed — the same day the header and the disclaimer carry.
 */
export function loanQuotesFileName(propertyTitle, date = new Date()) {
    const stamp = `${date.getMonth() + 1}.${date.getDate()}.${String(date.getFullYear()).slice(-2)}`;
    const property = (propertyTitle || '').trim();

    return property
        ? `Peak Capital Loan Quotes - ${property} ${stamp}`
        : `Peak Capital Loan Quotes ${stamp}`;
}

/**
 * Renumber loan quote columns so LOAN_QUOTES_ORDER matches the order the Loan
 * Quotes tab shows them in — what a drag, a copy or a delete leaves behind.
 *
 * Only rows the save already carries are flagged modified: the tab pads itself
 * out to six columns, and flagging those blank ones had every reorder insert
 * them as real (empty) quotes.
 */
export function reorderLoanQuotes(columns) {
    return (columns || []).map((column, index) => {
        if (!column || column.LOAN_QUOTES_ORDER === index) {
            return column;
        }

        const reordered = { ...column, LOAN_QUOTES_ORDER: index };

        if (column.ARCIMEDES_ROW_ID || column.LOAN_QUOTES_MODIFIED) {
            reordered.LOAN_QUOTES_MODIFIED = true;
        }

        return reordered;
    });
}

export function removeKeysFromObject(obj, keysToRetain) {
    Object.keys(obj).forEach(key => {
        if (!keysToRetain.includes(key)) {
            delete obj[key];
        }
    });
    return obj; // return the modified object if needed
}
export function formatDateTimeShort(dateStr) {
    if (!dateStr) return "";
    try {
        const date = new Date(dateStr);
        return date.toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    } catch (e) {
        return dateStr;
    }
};

function ordinalDayArcimedes(day) {
    if (day >= 11 && day <= 13) {
        return `${day}th`;
    }
    switch (day % 10) {
        case 1:
            return `${day}st`;
        case 2:
            return `${day}nd`;
        case 3:
            return `${day}rd`;
        default:
            return `${day}th`;
    }
}

const ARCIMEDES_MODIFIED_MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const ARCIMEDES_MODIFIED_TIME_ZONE = 'America/Chicago';

/**
 * Arcimedes row timestamp e.g. "2025-07-02 14:06:24" → "July 2nd, 9:06 AM".
 *
 * The separator is either a space (legacy Arcimedes) or a "T" (the Laravel Arc
 * gateway, which serves ISO-8601 like "2026-07-30T11:49:23.000000Z"). Only the
 * space form was accepted, so the Stats page's "Rates Last Updated" rendered as
 * an em dash even with a freshly synced row. (Claritask 546891)
 *
 * Rows are stamped in UTC and read here in Central, the clock the weekday 9:07
 * rates sync is scheduled on — the stored time reads hours ahead of it.
 */
export function formatArcimedesModified(raw) {
    if (!raw || typeof raw !== 'string') {
        return '';
    }
    const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})[T\s]+(\d{2}):(\d{2}):(\d{2})/);
    if (!m) {
        return '';
    }
    const stamped = new Date(Date.UTC(
        parseInt(m[1], 10),
        parseInt(m[2], 10) - 1,
        parseInt(m[3], 10),
        parseInt(m[4], 10),
        parseInt(m[5], 10),
        parseInt(m[6], 10),
    ));
    const central = new Intl.DateTimeFormat('en-US', {
        timeZone: ARCIMEDES_MODIFIED_TIME_ZONE,
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    })
        .formatToParts(stamped)
        .reduce((parts, part) => Object.assign(parts, {[part.type]: part.value}), {});
    const monthName = ARCIMEDES_MODIFIED_MONTH_NAMES[parseInt(central.month, 10) - 1];
    const day = ordinalDayArcimedes(parseInt(central.day, 10));
    return `${monthName} ${day}, ${central.hour}:${central.minute} ${central.dayPeriod}`;
}

export function formatDateUSA(dateString, showTime = false) {
    if (!dateString) return "";
    const date = new Date(dateString);

    const month = date.getMonth() + 1; // months start at 0
    const day = date.getDate();
    const year = date.getFullYear();

    let result = `${month}/${day}/${year}`;
    if (showTime) {
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? "PM" : "AM";
      const hour12 = hours % 12 || 12;
      const mins = minutes < 10 ? `0${minutes}` : minutes;
      result += ` ${hour12}:${mins} ${ampm}`;
    }
    return result;
  }

// US states (excluding territories) for dropdowns - same as Dropdown optionTypes 'states'
export const US_STATES = (() => {
    try {
        const u = require('united-states');
        const m = u.default || u;
        return Array.isArray(m) ? m.filter((el) => !el.isTerritory) : [];
    } catch (_) {
        return [];
    }
})();

export const COUNTRIES = [
    'United States',
    'Canada',
    'Mexico',
    'United Kingdom',
    'Germany',
    'France',
    'Spain',
    'Italy',
    'Australia',
    'Japan',
    'China',
    'India',
    'Brazil',
    'Netherlands',
    'Belgium',
    'Switzerland',
    'Austria',
    'Ireland',
    'Portugal',
    'Poland',
    'Sweden',
    'Norway',
    'Denmark',
    'Finland',
    'New Zealand',
    'South Korea',
    'Singapore',
    'South Africa',
    'Argentina',
    'Chile',
    'Colombia',
    'Other',
];
/**
 * Why the browser's speech recognition stopped, in words a user can act on.
 * Chrome reports these through SpeechRecognitionErrorEvent.error.
 */
export function speechErrorMessage(reason) {
    switch (reason) {
        case 'not-allowed':
        case 'service-not-allowed':
            return 'Microphone access is blocked for this site. Allow it in the browser address bar (the camera/mic icon), then try again.';
        case 'audio-capture':
            return 'No microphone was found. Check that one is connected and selected as the input device.';
        case 'network':
            return 'Speech recognition needs network access to Google\'s speech service and could not reach it.';
        default:
            return 'Speech recognition stopped unexpectedly' + (reason ? ` (${reason}).` : '.');
    }
}
